import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { uniq } from 'underscore';

import EmptyStateTimeline from '../../../../static/empty_state_timeline.svg?react';
import { formattedDateTime } from '../../../utils/dateUtils';
import { sessionStorageUtils } from '../../../utils/session-storage-utils';
import { isDefined } from '../../../utils/variables';
import { useDialog } from '../../hooks/use-dialog';
import { useToast } from '../../hooks/use-toast';
import { ProjectArtifactsContext } from '../../state/project-artifacts-state';
import { ProjectTimelineContext } from '../../state/project-timeline-state';
import { Center } from '../../styles/center';
import { Button } from '../button/button';
import { DataTable, DataTableColumns } from '../data-table/data-table';
import { Icon } from '../icon/icon';
import { IconPanel } from '../icon-panel/icon-panel';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { TimelineDialog } from './timeline-dialog';
import { getArtifactDisplayName, OnCreateOptions, OnDeleteOptions, OnUpdateOptions } from './types';

type TimelineDataItem = {
  index: number;
  id: string;
  collectionDate: Date;
  tiledModelName?: string;
  orthotileName?: string;
  pointCloudName?: string;
  comment?: string;
  geodataName?: string;
};

const pageSize = 20;

const tableStyle = {
  rowCell: {
    fontSize: '14px',
  },
  headerCell: {
    fontSize: '14px',
  },
};

const searchKeys: (keyof TimelineDataItem)[] = [
  'index',
  'collectionDate',
  'tiledModelName',
  'orthotileName',
  'pointCloudName',
  'comment',
  'geodataName',
] as const;

function filterItems(items: TimelineDataItem[], searchText: string) {
  const text = searchText.trim().toLowerCase();
  if (text.length === 0) {
    return items;
  }

  return items.filter((item) =>
    searchKeys.some((x) => item[x]?.toString()?.toLocaleLowerCase().includes(text)),
  );
}

function useArtifacts() {
  const { artifacts } = React.useContext(ProjectArtifactsContext);
  return artifacts;
}

function isGeodataTimeline(
  container: {
    orthoTileId?: string | null | undefined;
    tiledModelId?: string | null | undefined;
    pointCloudId?: string | null | undefined;
    gltftScanId?: string | null | undefined;
  },
  artifacts: ReturnType<typeof useArtifacts>,
) {
  const artifactIds = [
    container.orthoTileId,
    container.tiledModelId,
    container.pointCloudId,
    container.gltftScanId,
  ].filter((id): id is string => isDefined(id));

  const timelineArtifacts = artifactIds
    .map((id) => artifacts.find((a) => a.id === id))
    .filter(isDefined);

  const geodataIds = uniq(timelineArtifacts.map((x) => x.geodata?.id).filter(isDefined));
  const hasManualUpload = timelineArtifacts.some((x) => !isDefined(x.geodata));
  return geodataIds.length === 1 && !hasManualUpload;
}

const TimelineList = () => {
  const {
    timelines,
    isLoading: isLoadingTimelines,
    isRefetching,
    isMutating,
    createTimeline,
    updateTimeline,
    deleteTimeline,
    refetch,
  } = React.useContext(ProjectTimelineContext);

  const artifacts = useArtifacts();
  const { showToast } = useToast();
  const theme = useTheme();

  const timelineConfigurationDialog = useDialog();
  const { t } = useTranslation();

  const [selectedTimelineContainer, setSelectedTimelineContainer] = React.useState<
    (typeof timelines)[number] | undefined
  >();

  const [timelineList, setTimelineList] = useState<TimelineDataItem[]>([]);

  const hasGeodataText = useMemo(
    () => t('timelineList.columns.cloudProcess', { ns: 'components' }),
    [t],
  );
  const customTimelineText = useMemo(
    () => t('timelineList.columns.manualProcess', { ns: 'components' }),
    [t],
  );

  useEffect(() => {
    setTimelineList(
      timelines.map((container) => {
        const orthoTileArtifact = artifacts.find((a) => a.id === container.orthoTileId);
        const pointCloudArtifact = artifacts.find((a) => a.id === container.pointCloudId);
        const tiledModelArtifact = artifacts.find((a) => a.id === container.tiledModelId);
        const gltfScanArtifact = artifacts.find((a) => a.id === container.scanId);

        return {
          collectionDate: new Date(container.collectionDate),
          id: container.id,
          index: container.index,
          comment: container.comment ?? undefined,
          orthotileName: orthoTileArtifact ? getArtifactDisplayName(orthoTileArtifact) : '-',
          pointCloudName: pointCloudArtifact ? getArtifactDisplayName(pointCloudArtifact) : '-',
          tiledModelName:
            tiledModelArtifact || gltfScanArtifact
              ? getArtifactDisplayName((tiledModelArtifact ?? gltfScanArtifact)!)
              : '-',
          geodataName: isGeodataTimeline(container, artifacts)
            ? hasGeodataText
            : customTimelineText,
        };
      }),
    );
  }, [timelines, artifacts, hasGeodataText, customTimelineText]);

  const stringOrDash = (name: string | undefined | null) => name ?? '-';
  const tableColumns: DataTableColumns<TimelineDataItem> = useMemo(
    () => ({
      id: { hidden: true },
      index: {
        unfilterable: true,
        title: t('timelineList.columns.nr', { ns: 'components' }),
        width: '4em',
      },
      collectionDate: {
        unfilterable: true,
        title: t('timelineList.columns.collectionDate', { ns: 'components' }),
        formatter: formattedDateTime,
      },
      geodataName: {
        unfilterable: true,
        title: t('timelineList.columns.source', { ns: 'components' }),
        formatter: stringOrDash,
      },
      tiledModelName: {
        unfilterable: true,
        title: t('geodata.artifacts.3dModel', { ns: 'common' }),
        formatter: stringOrDash,
      },
      pointCloudName: {
        unfilterable: true,
        title: t('geodata.artifacts.pointCloud', { ns: 'common' }),
        formatter: stringOrDash,
      },
      orthotileName: {
        unfilterable: true,
        title: t('geodata.artifacts.orthoPhoto', { ns: 'common' }),
        formatter: stringOrDash,
      },
      comment: {
        unfilterable: true,
        title: t('timelineList.columns.comment', { ns: 'components' }),
        formatter: stringOrDash,
      },
    }),
    [t],
  );

  const onTimelineAdded = async (options: OnCreateOptions) => {
    await createTimeline(options);
  };

  const onTimelineUpdated = async (options: OnUpdateOptions) => {
    if (!isDefined(selectedTimelineContainer)) {
      return;
    }

    await updateTimeline({
      container: selectedTimelineContainer,
      options,
    });
  };

  const [searchText, setSearchText] = useState(
    sessionStorageUtils.getProjectItemOrDefault('timelineListText', ''),
  );
  const [items, setItems] = useState<TimelineDataItem[]>([]);
  useEffect(() => {
    setItems(filterItems(timelineList, searchText));
  }, [searchText, timelineList]);

  const onDeleteError = (error: Error) => {
    if (!axios.isAxiosError(error)) {
      return;
    }
    // This is not translated at this stage.
    // This does not follow the v1 standard of errors,
    // and updating the endpoint with correct error handling is out of scope.
    //
    //  So just mimic the previous behavior, as this has nothing to do with the React conversion.
    if (error.response?.data?.code === 'can_not_delete_timeline_with_connected_shared_views') {
      showToast({
        message: 'Tidslinjen går inte att ta bort eftersom den är kopplad till en delad vy.',
        type: 'error',
        title: 'Radera tidslinje',
      });
    }
  };

  const onTimelineDeleted = async (options: OnDeleteOptions) => {
    try {
      await deleteTimeline(options);
      setSelectedTimelineContainer(undefined);
    } catch (error) {
      onDeleteError(error as Error);
    }
  };

  const resolveSelectedTimelineContainer = (item: TimelineDataItem) => {
    const timelineContainer = timelines.find((timeline) => timeline.id === item.id);
    setSelectedTimelineContainer(timelineContainer);
    timelineConfigurationDialog.show();
  };

  const isLoading = isLoadingTimelines || isMutating;
  const isSpinning = isLoading || isRefetching;

  return (
    <OverlayLoader visible={isLoading}>
      <Component className="notranslate" direction="column">
        <Stack alignItems="anchor-center" direction="row" justifyContent="space-between">
          <TextBox
            autoFocus={true}
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            width={250}
            onChange={(e) => {
              setSearchText(e.target.value);
              sessionStorageUtils.setProjectItem('timelineListText', e.target.value);
            }}
          />
          <Stack alignItems="center" direction="row" spacing={1}>
            <Button
              color="primary"
              disabled={isLoading}
              variant="contained"
              onClick={() => timelineConfigurationDialog.show()}
            >
              {t('timelineList.addButton', { ns: 'components' })}
            </Button>
            <Icon
              color={theme.color.gray.dark}
              disabled={isSpinning}
              icon={isSpinning ? ['fas', 'spinner'] : ['fad', 'arrow-rotate-right']}
              size="lg"
              spin={isSpinning}
              title={t('refreshList', { ns: 'common' })}
              onClick={() => refetch()}
              onHoverStyle={{ icon: ['fas', isSpinning ? 'spinner' : 'arrow-rotate-right'] }}
            />
          </Stack>
        </Stack>
        <DataTable
          columns={tableColumns}
          fixedLayout={true}
          items={items}
          pageSize={pageSize}
          tableStyle={tableStyle}
          onRowClicked={resolveSelectedTimelineContainer}
        />

        {/** When no items exist, display empty timeline content. */}
        {isLoading === false && timelineList.length === 0 && (
          <IconPanel
            header={
              <StyledCenter>
                <EmptyStateTimeline />
              </StyledCenter>
            }
            responsiveness={{ componentPxHeightBreak: 0 }}
          >
            {{
              title: t('timelineList.emptyTitle', { ns: 'components' }),
              info: (
                <StyledH5>
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="timelineList.emptyInfo"
                    ns="components"
                  />
                </StyledH5>
              ),
            }}
          </IconPanel>
        )}

        {timelineConfigurationDialog.render(
          <TimelineDialog
            dialog={timelineConfigurationDialog}
            timeline={selectedTimelineContainer}
            onClose={() => setSelectedTimelineContainer(undefined)}
            onCreate={onTimelineAdded}
            onDelete={onTimelineDeleted}
            onUpdate={onTimelineUpdated}
          />,
        )}
      </Component>
    </OverlayLoader>
  );
};

const Component = styled(Stack)``;

const StyledCenter = styled(Center)`
  margin: 2em;
  margin-top: 5em;

  width: 12em;
  height: 12em;

  svg {
    transform: scale(2);
  }
`;
const StyledH5 = styled.h5`
  margin-bottom: 8em;
  color: ${(props) => props.theme.color.gray.dark};
`;

export { TimelineList };
